// src/components/aols/aeo/OLSupdates/OLS2900/OLS2905.tsx
import React from "react";
import { Layout, Typography, Breadcrumb } from "antd";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import { HomeOutlined } from "@ant-design/icons";
import { InlineShareButtons } from "sharethis-reactjs";
import Footer from "../../../../dev/utils/Footer/Footer";
import { updates2900 } from "./OLS2900";

const { Title, Paragraph } = Typography;

const UpdateContainer = styled.div`
  background: #000000;
  min-height: 100vh;
  padding: 40px 20px;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const UpdateContent = styled.div`
  max-width: 800px;
  width: 100%;
  background: #1a1a1a;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  text-align: center;
`;

const UpdateTitle = styled(Title)`
  color: #ffcc00 !important;
  margin-bottom: 20px;
`;

const UpdateParagraph = styled(Paragraph)`
  color: #fff !important;
  font-size: 16px;
  line-height: 1.8;
  margin-bottom: 20px;
`;

const UpdateLink = styled(Link)`
  color: #ffaa00 !important;
  text-decoration: none;
  font-size: 16px;
  transition: color 0.3s ease;

  &:hover {
    color: #ffcc00 !important;
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid #40c4ff;
    outline-offset: 2px;
  }
`;

const HighlightedText = styled.span`
  font-size: 48px;
  color: #ffcc00;
`;

const BreadcrumbWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 20px;
  z-index: 1001;

  .ant-breadcrumb a {
    color: #ffcc00;
    transition: color 0.3s ease;
  }

  .ant-breadcrumb a:hover {
    color: #ffaa00;
  }

  @media (max-width: 768px) {
    left: 10px;
    top: 5px;
    .ant-breadcrumb {
      font-size: 12px;
    }
  }
`;

const ShareThisWrapper = styled.div`
  margin-top: 20px;
  padding: 10px;
  background: #1a1a1a;
  border-radius: 8px;
  display: flex;
  justify-content: center;

  .st-btn {
    /* Remove uniform background color to allow platform-specific colors */
    border: 1px solid #ffcc00 !important; /* Add a subtle gold border */
    border-radius: 4px !important;
    transition: transform 0.3s ease;

    &:hover {
      transform: scale(1.05);
    }

    &:focus {
      outline: 2px solid #40c4ff;
      outline-offset: 2px;
    }
  }

  .st-total {
    color: #fff !important;
    font-size: 16px;
  }
`;

const CallToAction = styled.div`
  margin-top: 20px;
  padding: 15px;
  background: #ffcc00;
  border-radius: 8px;
  display: inline-block;
`;

const CallToActionText = styled(Paragraph)`
  color: #0427f4 !important;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const OLS2905: React.FC = () => {
  const update = updates2900.find((u) => u.id === 2905);

  if (!update) {
    return null; // Render nothing if the update is not found
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: "OLSLM: 2905.107.4582 - onelightsystem OLS",
    description: "Latest OLSLM update from onelightsystem OLS: OLSME price generator developed, timer visibility added, and promo price available now!",
    url: "https://www.olsme.com/olsupdates/ols2900/2905",
    datePublished: "2025-04-07",
    author: {
      "@type": "Organization",
      name: "onelightsystem OLS",
      url: "https://www.olsme.com",
    },
    publisher: {
      "@type": "Organization",
      name: "onelightsystem OLS",
      url: "https://www.olsme.com",
      logo: "https://www.olsme.com/logo.jpg",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://www.olsme.com/olsupdates/ols2900/2905",
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://www.olsme.com",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "OLS Updates",
          item: "https://www.olsme.com/olsupdates",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "OLS2900",
          item: "https://www.olsme.com/olsupdates/ols2900",
        },
        {
          "@type": "ListItem",
          position: 4,
          name: "OLSLM: 2905.107.4582",
          item: "https://www.olsme.com/olsupdates/ols2900/2905",
        },
      ],
    },
  };

  return (
    <Layout>
      <Helmet>
        <title>OLSLM: 2905.107.3405 - onelightsystem OLS</title>
        <meta
          name="description"
          content="Latest OLSLM update from onelightsystem OLS: OLSME price generator developed, timer visibility added, and promo price available now!"
        />
        <meta
          name="keywords"
          content="OLSLM update, onelightsystem OLS, OLSME price generator, Sun Light Meditation, OLS updates, Light Civilization, meditation education, promo price"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="onelightsystem OLS" />
        <link rel="canonical" href="https://www.olsme.com/olsupdates/ols2900/2905" />
        <meta property="og:title" content="OLSLM: 2905.107.3405 - onelightsystem OLS" />
        <meta
          property="og:description"
          content="Latest OLSLM update from onelightsystem OLS: OLSME price generator developed, timer visibility added, and promo price available now!"
        />
        <meta property="og:url" content="https://www.olsme.com/olsupdates/ols2900/2905" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta property="og:image:alt" content="onelightsystem OLS Logo for OLSLM Update" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="onelightsystem OLS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta name="twitter:title" content="OLSLM: 2905.107.3405 - onelightsystem OLS" />
        <meta
          name="twitter:description"
          content="Latest OLSLM update from onelightsystem OLS: OLSME price generator developed, timer visibility added, and promo price available now!"
        />
        <meta name="twitter:image" content="https://www.olsme.com/logo.jpg" />
        <meta name="twitter:image:alt" content="onelightsystem OLS Logo for OLSLM Update" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <BreadcrumbWrapper>
        <Breadcrumb>
          <Breadcrumb.Item>
            <Link to="/">
              <HomeOutlined /> Home
            </Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/olsupdates">OLS Updates</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/olsupdates/ols2900">OLS2900</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>OLSLM: 2905.107.3405</Breadcrumb.Item>
        </Breadcrumb>
      </BreadcrumbWrapper>

      <UpdateContainer>
        <UpdateContent>
          <UpdateTitle level={1}>OLSLM: 2905.107.3405</UpdateTitle>
          <UpdateParagraph>
            We’re thrilled to share the latest update from the onelightsystem OLS web development team! As part of our ongoing mission to enhance your meditative energy experience, we’ve introduced exciting new features to the OLSME platform.
          </UpdateParagraph>
          <UpdateParagraph>
            <strong>Introducing the OLSME Price Generator:</strong> Now you can easily calculate your personalized subscription price for 2025 and 2026 with our new pre-evaluation tool. Whether you’re an individual seeking inner illumination or a corporate looking to boost well-being, this tool helps you plan your journey with OLSME. Check it out on the <UpdateLink to="/olsme">OLSME page</UpdateLink> and discover your promo price—available for a limited time only!
          </UpdateParagraph>
          <UpdateParagraph>
            <strong>Stay Connected with the OLSME Timer:</strong> We’ve added a visible timer in the top-right corner of the page, so you’ll always know when the next OLSME session begins. This feature ensures you’re more connected than ever, helping you prepare for your meditative rest session with ease. Don’t miss out—join the session and experience the power of Sun Light Meditation!
          </UpdateParagraph>
          <UpdateParagraph>
            <strong>Act Now—Promo Prices Won’t Last!</strong> Don’t wait until tomorrow to unlock your OLSME subscription at a special promo price. These offers are available for a limited time, so seize the opportunity to join the Light Civilization today. Alternatively, become an OLS student and enjoy an OLSME subscription as part of your membership—learn more on the <UpdateLink to="/OLSStudentProspect">OLS Student Prospect page</UpdateLink>.
          </UpdateParagraph>
          <CallToAction>
            <CallToActionText>
              Get Started Today—Your Journey Awaits!
            </CallToActionText>
          </CallToAction>

          {/* ShareThis Plugin */}
          <ShareThisWrapper>
            <InlineShareButtons
              config={{
                alignment: "center",
                color: "social", // Ensure platform-specific colors are used
                enabled: true,
                font_size: 16,
                labels: "cta",
                language: "en",
                networks: [
                  "whatsapp",
                  "linkedin",
                  "messenger",
                  "facebook",
                  "twitter",
                  "blogger",
                  "pinterest",
                  "sms",
                ],
                padding: 12,
                radius: 4,
                show_total: true,
                size: 20,
                min_count: 10,
                url: "https://www.olsme.com/olsupdates/ols2900/2905",
                image: "https://www.olsme.com/logo.jpg",
                description: "Latest OLSLM update from onelightsystem OLS: OLSME price generator developed, timer visibility added, and promo price available now!",
                title: "OLSLM: 2905.107.3405 - onelightsystem OLS",
                message: "Check out this latest OLSLM update from onelightsystem OLS!",
                subject: "OLSLM Update: 2905.107.3405",
                username: "onelightsystem",
              }}
            />
          </ShareThisWrapper>
        </UpdateContent>
      </UpdateContainer>

      <Footer />
    </Layout>
  );
};

export default OLS2905;